import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Briefcase, Upload, Loader2, Image as ImageIcon, Film, FileText, Archive, Plus, Trash2, Info, CheckCircle2 } from 'lucide-react';
import { Card } from '../ui/Card';
import Button from '../ui/Button';
import { SectionTitle, useFmt } from './tmShared';
import trafficManagerService, { BusinessData, BusinessResult, Material, TrafficManager } from '../../services/trafficManagerService';

const inputCls = 'w-full px-3 py-2 text-base sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500';
const labelCls = 'block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1';

// ---------------------------------------------------------------------------
// Dados do negócio
// ---------------------------------------------------------------------------
const BusinessForm: React.FC<{ manager: TrafficManager; onSaved: (m: TrafficManager) => void }> = ({ manager, onSaved }) => {
  const { t } = useTranslation();
  const [b, setB] = useState<BusinessData>(() => ({ ...(manager.metadata?.business || {}) }));
  const [saving, setSaving] = useState(false);
  const set = (k: keyof BusinessData, v: string) => setB((x) => ({ ...x, [k]: v }));
  const campos: [keyof BusinessData, string, string, boolean][] = [
    ['offer', t('trafficManager.business.offer', 'O que você vende e a oferta de agora'), t('trafficManager.business.offerPh', 'Ex.: VPS no Brasil a partir de R$ 29/mês, R$ 300 de crédito para testar'), true],
    ['differentiators', t('trafficManager.business.diff', 'Diferenciais (o robô pode usar nos anúncios)'), t('trafficManager.business.diffPh', 'Ex.: nota fiscal, suporte em português, datacenter em SP'), true],
    ['avoid', t('trafficManager.business.avoid', 'O que nunca dizer ou prometer'), t('trafficManager.business.avoidPh', 'Ex.: não prometer "grátis para sempre"; não citar concorrente pelo nome'), true],
    ['ideal_customer', t('trafficManager.business.ideal', 'Cliente ideal'), t('trafficManager.business.idealPh', 'Ex.: startups e agências que pagam AWS em dólar'), true],
    ['avg_ticket', t('trafficManager.business.ticket', 'Ticket médio'), t('trafficManager.business.ticketPh', 'Ex.: R$ 180/mês'), false],
    ['customer_value', t('trafficManager.business.ltv', 'Quanto vale um cliente (no total)'), t('trafficManager.business.ltvPh', 'Ex.: R$ 2.000 em 12 meses'), false],
    ['max_cpl', t('trafficManager.business.maxCpl', 'Quanto pode pagar por um lead'), t('trafficManager.business.maxCplPh', 'Ex.: até R$ 60'), false],
    ['close_rate', t('trafficManager.business.close', 'De cada 10 leads, quantos viram venda'), t('trafficManager.business.closePh', 'Ex.: 2'), false],
    ['sales_cycle', t('trafficManager.business.cycle', 'Tempo até fechar'), t('trafficManager.business.cyclePh', 'Ex.: 7 dias'), false],
    ['seasonality', t('trafficManager.business.season', 'Sazonalidade'), t('trafficManager.business.seasonPh', 'Ex.: dezembro fraco, março forte'), false],
    ['competitors', t('trafficManager.business.competitors', 'Concorrentes'), t('trafficManager.business.competitorsPh', 'Ex.: Hostinger, Contabo, DigitalOcean'), false]
  ];
  const salvar = async () => {
    setSaving(true);
    try {
      const m = await trafficManagerService.update(manager.id, { business: { ...b, results: manager.metadata?.business?.results || [] } });
      onSaved(m);
      toast.success(t('trafficManager.business.saved', 'Salvo. O robô usa a partir da próxima rodada.'));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('trafficManager.saveError', 'Não foi possível salvar'));
    } finally { setSaving(false); }
  };
  return (
    <Card>
      <SectionTitle icon={Briefcase}>{t('trafficManager.business.title', 'Dados do negócio')}</SectionTitle>
      <p className="text-sm text-gray-500 -mt-2 mb-4">{t('trafficManager.business.help', 'O que um gestor perguntaria no primeiro dia. O robô usa para julgar o que é caro, escrever anúncios só com fatos e não prometer o que você não entrega. Tudo é opcional; o perfil da empresa em Configurações também entra.')}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {campos.map(([k, label, ph, longo]) => (
          <div key={k} className={longo ? 'md:col-span-2' : ''}>
            <label className={labelCls} htmlFor={`tm-b-${k}`}>{label}</label>
            {longo
              ? <textarea id={`tm-b-${k}`} className={`${inputCls} min-h-[64px]`} value={(b[k] as string) || ''} onChange={(e) => set(k, e.target.value)} placeholder={ph} maxLength={1500} />
              : <input id={`tm-b-${k}`} className={inputCls} value={(b[k] as string) || ''} onChange={(e) => set(k, e.target.value)} placeholder={ph} maxLength={200} />}
          </div>
        ))}
        <div className="md:col-span-2">
          <label className={labelCls} htmlFor="tm-b-notes">{t('trafficManager.business.notes', 'Outras informações')}</label>
          <textarea id="tm-b-notes" className={`${inputCls} min-h-[90px]`} value={b.notes || ''} onChange={(e) => set('notes', e.target.value)} maxLength={4000}
            placeholder={t('trafficManager.business.notesPh', 'Lançamentos, promoções com data, regiões que você atende melhor, qualquer coisa que ajude.') as string} />
        </div>
      </div>
      <div className="mt-4 flex justify-end"><Button onClick={salvar} isLoading={saving}>{t('common.save', 'Salvar')}</Button></div>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Resultados reais (o que a plataforma não vê: lead qualificado, venda, receita)
// ---------------------------------------------------------------------------
const RealResults: React.FC<{ manager: TrafficManager; onSaved: (m: TrafficManager) => void }> = ({ manager, onSaved }) => {
  const { t } = useTranslation();
  const f = useFmt();
  const lista = manager.metadata?.business?.results || [];
  const campanhas = manager.snapshot?.campanhas || [];
  const vazio: BusinessResult = { period: new Date().toISOString().slice(0, 7), campaign: '', leads: null, sales: null, revenue: null, note: '' };
  const [novo, setNovo] = useState<BusinessResult>(vazio);
  const [saving, setSaving] = useState(false);
  const gravar = async (results: BusinessResult[]) => {
    setSaving(true);
    try {
      const m = await trafficManagerService.update(manager.id, { business: { ...(manager.metadata?.business || {}), results } });
      onSaved(m);
      return true;
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('trafficManager.saveError', 'Não foi possível salvar'));
      return false;
    } finally { setSaving(false); }
  };
  const num = (v: string) => (v === '' ? null : Number(v));
  const adicionar = async () => {
    if (!novo.period || (novo.leads == null && novo.sales == null && novo.revenue == null)) { toast.error(t('trafficManager.results.fill', 'Informe o período e pelo menos leads, vendas ou receita') as string); return; }
    if (await gravar([...lista, novo])) setNovo(vazio);
  };
  return (
    <Card>
      <SectionTitle icon={CheckCircle2}>{t('trafficManager.results.title', 'Resultados reais')}</SectionTitle>
      <p className="text-sm text-gray-500 -mt-2 mb-4">{t('trafficManager.results.help', 'A plataforma conta clique no WhatsApp e formulário; só você sabe quantos viraram cliente. Com isso o robô julga as campanhas pela venda, não só pela conversão.')}</p>
      {lista.length > 0 && (
        <div className="overflow-x-auto mb-4">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-gray-500 border-b border-gray-100 dark:border-gray-700">
              <th className="py-2 pr-3 font-medium">{t('trafficManager.results.period', 'Período')}</th>
              <th className="py-2 pr-3 font-medium">{t('trafficManager.campaigns.campaign', 'Campanha')}</th>
              <th className="py-2 pr-3 font-medium text-right">{t('trafficManager.results.leads', 'Leads bons')}</th>
              <th className="py-2 pr-3 font-medium text-right">{t('trafficManager.results.sales', 'Vendas')}</th>
              <th className="py-2 pr-3 font-medium text-right">{t('trafficManager.results.revenue', 'Receita')}</th>
              <th className="py-2 pr-3 font-medium">{t('trafficManager.results.note', 'Nota')}</th>
              <th className="py-2 w-8" />
            </tr></thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {[...lista].reverse().map((r) => (
                <tr key={r.id || `${r.period}-${r.campaign}`}>
                  <td className="py-2 pr-3 whitespace-nowrap">{r.period}</td>
                  <td className="py-2 pr-3 text-gray-600 dark:text-gray-300 max-w-[14rem] truncate">{r.campaign || t('trafficManager.results.all', 'todas')}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{r.leads ?? '—'}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{r.sales ?? '—'}</td>
                  <td className="py-2 pr-3 text-right tabular-nums whitespace-nowrap">{r.revenue != null ? f.money(r.revenue, manager.currency) : '—'}</td>
                  <td className="py-2 pr-3 text-gray-500 max-w-[16rem] truncate">{r.note || ''}</td>
                  <td className="py-2"><button type="button" disabled={saving} onClick={() => gravar(lista.filter((x) => x !== r))} className="p-1.5 text-gray-400 hover:text-red-600" aria-label={t('trafficManager.results.remove', 'Remover') as string}><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
        <div><label className={labelCls} htmlFor="tm-r-period">{t('trafficManager.results.period', 'Período')}</label><input id="tm-r-period" type="month" className={inputCls} value={novo.period} onChange={(e) => setNovo({ ...novo, period: e.target.value })} /></div>
        <div className="col-span-2 md:col-span-2"><label className={labelCls} htmlFor="tm-r-camp">{t('trafficManager.campaigns.campaign', 'Campanha')}</label>
          <select id="tm-r-camp" className={inputCls} value={novo.campaign || ''} onChange={(e) => setNovo({ ...novo, campaign: e.target.value })}>
            <option value="">{t('trafficManager.results.all', 'todas')}</option>
            {campanhas.map((c) => <option key={c.id} value={c.nome}>{c.nome}</option>)}
          </select>
        </div>
        <div><label className={labelCls} htmlFor="tm-r-leads">{t('trafficManager.results.leads', 'Leads bons')}</label><input id="tm-r-leads" type="number" min={0} className={inputCls} value={novo.leads ?? ''} onChange={(e) => setNovo({ ...novo, leads: num(e.target.value) })} /></div>
        <div><label className={labelCls} htmlFor="tm-r-sales">{t('trafficManager.results.sales', 'Vendas')}</label><input id="tm-r-sales" type="number" min={0} className={inputCls} value={novo.sales ?? ''} onChange={(e) => setNovo({ ...novo, sales: num(e.target.value) })} /></div>
        <div><label className={labelCls} htmlFor="tm-r-rev">{t('trafficManager.results.revenue', 'Receita')}</label><input id="tm-r-rev" type="number" min={0} step="0.01" className={inputCls} value={novo.revenue ?? ''} onChange={(e) => setNovo({ ...novo, revenue: num(e.target.value) })} /></div>
        <div className="col-span-2 md:col-span-5"><label className={labelCls} htmlFor="tm-r-note">{t('trafficManager.results.note', 'Nota')}</label><input id="tm-r-note" className={inputCls} value={novo.note || ''} onChange={(e) => setNovo({ ...novo, note: e.target.value })} maxLength={500} placeholder={t('trafficManager.results.notePh', 'Ex.: muitos leads de estudante, poucos de empresa') as string} /></div>
        <div className="col-span-2 md:col-span-1"><Button className="w-full" onClick={adicionar} isLoading={saving} leftIcon={<Plus className="w-4 h-4" />}>{t('trafficManager.results.add', 'Adicionar')}</Button></div>
      </div>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Materiais (artes, vídeos e textos)
// ---------------------------------------------------------------------------
const lerMidia = (file: File): Promise<{ width?: number; height?: number; duration?: number }> => new Promise((res) => {
  const url = URL.createObjectURL(file);
  if (file.type.startsWith('image/')) {
    const img = new window.Image();
    img.onload = () => { res({ width: img.naturalWidth, height: img.naturalHeight }); URL.revokeObjectURL(url); };
    img.onerror = () => { res({}); URL.revokeObjectURL(url); };
    img.src = url;
  } else if (file.type.startsWith('video/')) {
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.onloadedmetadata = () => { res({ width: v.videoWidth, height: v.videoHeight, duration: Math.round(v.duration) }); URL.revokeObjectURL(url); };
    v.onerror = () => { res({}); URL.revokeObjectURL(url); };
    v.src = url;
  } else res({});
});

const MaterialCard: React.FC<{ manager: TrafficManager; m: Material; onChange: (m: Material | null) => void }> = ({ manager, m, onChange }) => {
  const { t } = useTranslation();
  const [notes, setNotes] = useState(m.notes || '');
  const [product, setProduct] = useState(m.product || '');
  const [name, setName] = useState(m.name);
  const salvar = async (patch: Partial<Material>) => {
    try { onChange(await trafficManagerService.updateMaterial(manager.id, m.id, patch)); } catch { toast.error(t('trafficManager.saveError', 'Não foi possível salvar')); }
  };
  const Icon = m.kind === 'image' ? ImageIcon : m.kind === 'video' ? Film : FileText;
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800/40 flex flex-col">
      <div className="aspect-video bg-gray-100 dark:bg-gray-900 flex items-center justify-center overflow-hidden">
        {m.kind === 'image' && m.url && <img src={m.url} alt={m.name} className="w-full h-full object-contain" loading="lazy" />}
        {m.kind === 'video' && m.url && <video src={m.url} className="w-full h-full object-contain" controls preload="metadata" />}
        {m.kind === 'text' && <p className="p-3 text-xs text-gray-600 dark:text-gray-300 line-clamp-6 whitespace-pre-wrap self-start">{m.content}</p>}
      </div>
      <div className="p-3 space-y-2 flex-1">
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <Icon className="w-3.5 h-3.5" />
          {m.meta?.width ? `${m.meta.width}×${m.meta.height}` : null}{m.meta?.duration ? ` · ${m.meta.duration}s` : null}
          <span className="ml-auto">{m.usage.length ? t('trafficManager.materials.usedN', 'usado em {{n}} anúncio(s)', { n: m.usage.length }) : t('trafficManager.materials.unused', 'ainda não usado')}</span>
        </div>
        <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} onBlur={() => name !== m.name && salvar({ name })} aria-label={t('trafficManager.materials.name', 'Nome') as string} />
        <input className={inputCls} value={product} onChange={(e) => setProduct(e.target.value)} onBlur={() => product !== (m.product || '') && salvar({ product })} placeholder={t('trafficManager.materials.product', 'Produto ou oferta') as string} aria-label={t('trafficManager.materials.product', 'Produto ou oferta') as string} />
        <textarea className={`${inputCls} min-h-[56px]`} value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={() => notes !== (m.notes || '') && salvar({ notes })}
          placeholder={t('trafficManager.materials.notesPh', 'O que é e quando usar. Ex.: arte da promoção de outubro, só para a campanha Cloud') as string} aria-label={t('trafficManager.materials.notes', 'Notas') as string} />
        {m.usage.length > 0 && <div className="text-[11px] text-gray-500">{m.usage.slice(-3).map((u) => `${u.campaign_name || ''} (${u.platform})`).join(' · ')}</div>}
      </div>
      <div className="px-3 pb-3">
        <button type="button" onClick={async () => { await salvar({ status: 'archived' }); onChange(null); }} className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-red-600">
          <Archive className="w-3.5 h-3.5" />{t('trafficManager.materials.archive', 'Tirar da lista do robô')}
        </button>
      </div>
    </div>
  );
};

const Materials: React.FC<{ manager: TrafficManager }> = ({ manager }) => {
  const { t } = useTranslation();
  const [lista, setLista] = useState<Material[] | null>(null);
  const [subindo, setSubindo] = useState<string | null>(null);
  const [texto, setTexto] = useState('');
  const [nomeTexto, setNomeTexto] = useState('');
  const arquivo = useRef<HTMLInputElement>(null);
  const carregar = useCallback(async () => {
    try { setLista(await trafficManagerService.materials(manager.id)); } catch { setLista([]); }
  }, [manager.id]);
  useEffect(() => { carregar(); }, [carregar]);

  const subir = async (files: FileList | null) => {
    if (!files || !files.length) return;
    for (const file of Array.from(files).slice(0, 10)) {
      const ehTexto = /\.(txt|csv|md)$/i.test(file.name) || file.type.startsWith('text/');
      const ehVideo = file.type.startsWith('video/');
      const ehImagem = file.type.startsWith('image/');
      if (!ehTexto && !ehVideo && !ehImagem) { toast.error(t('trafficManager.materials.badType', '{{f}}: use imagem, vídeo ou .txt/.csv', { f: file.name }) as string); continue; }
      if (ehImagem && file.size > 10 * 1024 * 1024) { toast.error(t('trafficManager.materials.tooBigImg', '{{f}}: imagem acima de 10 MB', { f: file.name }) as string); continue; }
      if (ehVideo && file.size > 200 * 1024 * 1024) { toast.error(t('trafficManager.materials.tooBigVid', '{{f}}: vídeo acima de 200 MB', { f: file.name }) as string); continue; }
      setSubindo(file.name);
      try {
        if (ehTexto) {
          const content = (await file.text()).slice(0, 50000);
          await trafficManagerService.addMaterial(manager.id, { kind: 'text', name: file.name, content });
        } else {
          const meta = await lerMidia(file);
          const up = await trafficManagerService.uploadFile(file);
          await trafficManagerService.addMaterial(manager.id, { kind: ehVideo ? 'video' : 'image', name: file.name.replace(/\.[^.]+$/, ''), url: up.url, meta: { ...meta, size: up.size, mime: file.type } });
        }
      } catch (e: any) {
        toast.error(e?.response?.data?.message || t('trafficManager.materials.uploadError', 'Não foi possível subir {{f}}', { f: file.name }));
      }
    }
    setSubindo(null);
    if (arquivo.current) arquivo.current.value = '';
    carregar();
  };
  const salvarTexto = async () => {
    if (!texto.trim()) return;
    try {
      await trafficManagerService.addMaterial(manager.id, { kind: 'text', name: nomeTexto.trim() || t('trafficManager.materials.text', 'Texto') as string, content: texto });
      setTexto(''); setNomeTexto(''); carregar();
    } catch (e: any) { toast.error(e?.response?.data?.message || t('trafficManager.saveError', 'Não foi possível salvar')); }
  };
  return (
    <Card>
      <SectionTitle icon={Upload}>{t('trafficManager.materials.title', 'Materiais para os anúncios')}</SectionTitle>
      <p className="text-sm text-gray-500 -mt-2 mb-4">{t('trafficManager.materials.help', 'Artes e vídeos que o robô pode usar quando criar anúncios novos (Meta: imagem e vídeo; Google: textos), e textos como tabela de preços, depoimentos ou briefing. Diga nas notas o que é e quando usar: ele respeita.')}</p>
      <div
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={(e) => { e.preventDefault(); subir(e.dataTransfer.files); }}
        className="rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 p-6 text-center"
      >
        {subindo
          ? <div className="flex items-center justify-center gap-2 text-sm text-gray-600 dark:text-gray-300"><Loader2 className="w-4 h-4 animate-spin" />{t('trafficManager.materials.uploading', 'Enviando {{f}}…', { f: subindo })}</div>
          : (
            <>
              <Upload className="w-6 h-6 text-gray-400 mx-auto" />
              <p className="mt-2 text-sm text-gray-700 dark:text-gray-200">{t('trafficManager.materials.drop', 'Arraste aqui ou escolha arquivos')}</p>
              <p className="text-xs text-gray-500">{t('trafficManager.materials.limits', 'Imagem até 10 MB, vídeo até 200 MB, texto .txt ou .csv')}</p>
              <Button variant="secondary" size="sm" className="mt-3" onClick={() => arquivo.current?.click()}>{t('trafficManager.materials.choose', 'Escolher arquivos')}</Button>
              <input ref={arquivo} type="file" multiple accept="image/*,video/*,.txt,.csv,.md,text/plain,text/csv" className="hidden" onChange={(e) => subir(e.target.files)} />
            </>
          )}
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-start">
        <div className="space-y-2">
          <input className={inputCls} value={nomeTexto} onChange={(e) => setNomeTexto(e.target.value)} placeholder={t('trafficManager.materials.textName', 'Nome do texto (ex.: Tabela de preços)') as string} aria-label={t('trafficManager.materials.textName', 'Nome do texto (ex.: Tabela de preços)') as string} />
          <textarea className={`${inputCls} min-h-[80px]`} value={texto} onChange={(e) => setTexto(e.target.value)} maxLength={50000}
            placeholder={t('trafficManager.materials.textPh', 'Ou cole um texto: preços, depoimentos de cliente, perguntas frequentes, briefing de campanha…') as string} aria-label={t('trafficManager.materials.text', 'Texto') as string} />
        </div>
        <Button variant="secondary" onClick={salvarTexto} disabled={!texto.trim()} leftIcon={<Plus className="w-4 h-4" />}>{t('trafficManager.materials.addText', 'Adicionar texto')}</Button>
      </div>
      {lista === null && <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>}
      {lista && lista.length === 0 && <p className="mt-4 text-sm text-gray-500 flex gap-2"><Info className="w-4 h-4 shrink-0 mt-0.5" />{t('trafficManager.materials.empty', 'Nenhum material ainda. Sem material, o robô só cria anúncio de texto no Google com os dados do negócio.')}</p>}
      {lista && lista.length > 0 && (
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {lista.map((m) => <MaterialCard key={m.id} manager={manager} m={m} onChange={(x) => setLista((l) => (l || []).flatMap((y) => (y.id !== m.id ? [y] : x ? [x] : [])))} />)}
        </div>
      )}
    </Card>
  );
};

/** Aba "Negócio e materiais": o que o responsável conta e entrega ao robô. */
const BusinessTab: React.FC<{ manager: TrafficManager; onManagerChange: (m: TrafficManager) => void }> = ({ manager, onManagerChange }) => (
  <div className="space-y-4 sm:space-y-6">
    <BusinessForm manager={manager} onSaved={onManagerChange} />
    <RealResults manager={manager} onSaved={onManagerChange} />
    <Materials manager={manager} />
  </div>
);

export default BusinessTab;
